import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";

export interface PlanLimits {
  tierName: string;
  displayName: string;
  maxActiveRoles: number | null;
  maxCandidatesPerMonth: number | null;
  maxTeamSeats: number | null;
  roleExpiryDays: number | null;
  canRemoveBranding: boolean;
  canEditTemplates: boolean;
  smsEnabled: boolean;
  whiteLabel: boolean;
  clientPortal: boolean;
  priceNzdMonthly: number | null;
  isFree: boolean;
  isStarter: boolean;
  isGrowth: boolean;
  isAgency: boolean;
  isEnterprise: boolean;
}

export interface UsageStats {
  activeRoles: number;
  candidatesThisMonth: number;
  teamSeats: number;
  subscriptionRenewalDate: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  freeRoleExpiresAt: string | null;
}

export interface PlanEnforcement {
  plan: PlanLimits;
  usage: UsageStats;
  canCreateRole: boolean;
  canInviteCandidate: boolean;
  canAddTeamMember: boolean;
  rolesRemaining: number | null;
  candidatesRemaining: number | null;
  usagePercentRoles: number;
  usagePercentCandidates: number;
  isNearRoleLimit: boolean;
  isNearCandidateLimit: boolean;
  isAtRoleLimit: boolean;
  isAtCandidateLimit: boolean;
  showUpgradePrompt: boolean;
  upgradeReason: string | null;
  daysUntilBillingRenewal: number | null;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  growth: 2,
  professional: 2,
  agency: 3,
  enterprise: 4,
};

function buildUpgradeReason(plan: PlanLimits, usage: UsageStats): string | null {
  if (plan.maxActiveRoles !== null && usage.activeRoles >= plan.maxActiveRoles) {
    const next = getNextTier(plan.tierName);
    return `You've used all ${plan.maxActiveRoles} of your active role slots on ${plan.displayName}. ${next ? `Upgrade to ${next} to manage more roles.` : "Contact us to unlock unlimited roles."}`;
  }
  if (plan.maxCandidatesPerMonth !== null && usage.candidatesThisMonth >= plan.maxCandidatesPerMonth) {
    const next = getNextTier(plan.tierName);
    return `You've sent ${plan.maxCandidatesPerMonth} candidate assessments this month on ${plan.displayName}. ${next ? `Upgrade to ${next} to assess more candidates.` : "Contact us for unlimited assessments."}`;
  }
  if (plan.isFree && plan.maxActiveRoles !== null && usage.activeRoles / plan.maxActiveRoles >= 0.8) {
    return `You're using ${usage.activeRoles} of ${plan.maxActiveRoles} role slots on ${plan.displayName}. Upgrade to unlock more roles.`;
  }
  return null;
}

function getNextTier(currentTier: string): string | null {
  const order = TIER_ORDER[currentTier] ?? 0;
  const tiers = ["Free", "Starter", "Growth", "Agency", "Enterprise"];
  const tierNames = ["free", "starter", "growth", "agency", "enterprise"];
  const nextIdx = order + 1;
  return nextIdx < tiers.length ? tiers[nextIdx] : null;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

const DEFAULT_PLAN: PlanLimits = {
  tierName: "free",
  displayName: "Try Vexis",
  maxActiveRoles: 1,
  maxCandidatesPerMonth: 3,
  maxTeamSeats: 1,
  roleExpiryDays: 30,
  canRemoveBranding: false,
  canEditTemplates: false,
  smsEnabled: true,
  whiteLabel: false,
  clientPortal: false,
  priceNzdMonthly: 0,
  isFree: true,
  isStarter: false,
  isGrowth: false,
  isAgency: false,
  isEnterprise: false,
};

const DEFAULT_USAGE: UsageStats = {
  activeRoles: 0,
  candidatesThisMonth: 0,
  teamSeats: 1,
  subscriptionRenewalDate: null,
  trialEndsAt: null,
  currentPeriodStart: null,
  freeRoleExpiresAt: null,
};

export function usePlan(): PlanEnforcement {
  const [plan, setPlan] = useState<PlanLimits>(DEFAULT_PLAN);
  const [usage, setUsage] = useState<UsageStats>(DEFAULT_USAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const [billingRes, usageRes] = await Promise.all([
          fetch("/api/billing/status", { credentials: "include" }),
          fetch("/api/agent/usage-stats", { credentials: "include" }),
        ]);

        if (!billingRes.ok || !usageRes.ok) {
          throw new Error("Failed to load plan data");
        }

        const billing = await billingRes.json();
        const usageData = await usageRes.json();

        if (cancelled) return;

        const tierName: string = billing.tier ?? "free";
        const resolvedPlan: PlanLimits = {
          tierName,
          displayName: billing.displayName ?? "Try Vexis",
          maxActiveRoles: billing.maxActiveRoles ?? null,
          maxCandidatesPerMonth: billing.maxCandidatesPerMonth ?? null,
          maxTeamSeats: billing.maxTeamSeats ?? null,
          roleExpiryDays: billing.roleExpiryDays ?? null,
          canRemoveBranding: billing.canRemoveBranding ?? false,
          canEditTemplates: billing.canEditTemplates ?? false,
          smsEnabled: billing.smsEnabled ?? true,
          whiteLabel: billing.whiteLabel ?? false,
          clientPortal: billing.clientPortal ?? false,
          priceNzdMonthly: billing.priceNzdMonthly ?? null,
          isFree: tierName === "free",
          isStarter: tierName === "starter",
          isGrowth: tierName === "growth" || tierName === "professional",
          isAgency: tierName === "agency",
          isEnterprise: tierName === "enterprise",
        };

        const resolvedUsage: UsageStats = {
          activeRoles: usageData.activeRoles ?? 0,
          candidatesThisMonth: usageData.candidatesThisMonth ?? 0,
          teamSeats: usageData.teamSeats ?? 1,
          subscriptionRenewalDate: usageData.subscriptionRenewalDate ?? null,
          trialEndsAt: usageData.trialEndsAt ?? null,
          currentPeriodStart: usageData.currentPeriodStart ?? null,
          freeRoleExpiresAt: usageData.freeRoleExpiresAt ?? null,
        };

        setPlan(resolvedPlan);
        setUsage(resolvedUsage);
      } catch (err) {
        if (!cancelled) setError("Failed to load plan information");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [tick]);

  const { maxActiveRoles, maxCandidatesPerMonth, maxTeamSeats } = plan;
  const { activeRoles, candidatesThisMonth, teamSeats } = usage;

  const usagePercentRoles = maxActiveRoles ? Math.min(100, (activeRoles / maxActiveRoles) * 100) : 0;
  const usagePercentCandidates = maxCandidatesPerMonth ? Math.min(100, (candidatesThisMonth / maxCandidatesPerMonth) * 100) : 0;

  const isNearRoleLimit = maxActiveRoles !== null && usagePercentRoles >= 80;
  const isNearCandidateLimit = maxCandidatesPerMonth !== null && usagePercentCandidates >= 80;
  const isAtRoleLimit = maxActiveRoles !== null && activeRoles >= maxActiveRoles;
  const isAtCandidateLimit = maxCandidatesPerMonth !== null && candidatesThisMonth >= maxCandidatesPerMonth;

  const canCreateRole = !isAtRoleLimit;
  const canInviteCandidate = !isAtCandidateLimit;
  const canAddTeamMember = maxTeamSeats === null || teamSeats < maxTeamSeats;

  const rolesRemaining = maxActiveRoles !== null ? Math.max(0, maxActiveRoles - activeRoles) : null;
  const candidatesRemaining = maxCandidatesPerMonth !== null ? Math.max(0, maxCandidatesPerMonth - candidatesThisMonth) : null;

  const showUpgradePrompt = isAtRoleLimit || isAtCandidateLimit || (isNearRoleLimit && plan.isFree);
  const upgradeReason = showUpgradePrompt ? buildUpgradeReason(plan, usage) : null;

  const now = new Date();
  const renewalDate = usage.subscriptionRenewalDate ? new Date(usage.subscriptionRenewalDate) : null;
  const daysUntilBillingRenewal = renewalDate ? daysBetween(now, renewalDate) : null;

  const trialDate = usage.trialEndsAt ? new Date(usage.trialEndsAt) : null;
  const isTrialActive = trialDate !== null && trialDate > now;
  const trialDaysRemaining = isTrialActive && trialDate ? daysBetween(now, trialDate) : null;

  return {
    plan,
    usage,
    canCreateRole,
    canInviteCandidate,
    canAddTeamMember,
    rolesRemaining,
    candidatesRemaining,
    usagePercentRoles,
    usagePercentCandidates,
    isNearRoleLimit,
    isNearCandidateLimit,
    isAtRoleLimit,
    isAtCandidateLimit,
    showUpgradePrompt,
    upgradeReason,
    daysUntilBillingRenewal,
    isTrialActive,
    trialDaysRemaining,
    isLoading,
    error,
    refresh,
  };
}

export function navigateToUpgrade(
  navigate: (path: string) => void,
  reason: string,
  currentTier: string,
  recommendedTier?: string,
): void {
  const params = new URLSearchParams({
    current: currentTier,
    reason,
    ...(recommendedTier ? { recommend: recommendedTier } : {}),
  });
  navigate(`/pricing?${params.toString()}`);
}
